import { useState, useEffect } from 'react';
import { ArrowLeft, PenTool, Download, Calendar, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';

const Signatures = () => {
  const [signatures, setSignatures] = useState<any[]>([]);
  const [pendingSignatures, setPendingSignatures] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSignatures();
  }, []);

  const fetchSignatures = async () => {
    try {
      // Fetch completed signatures
      const { data: completedSigs } = await supabase
        .from('signature_logs')
        .select(`
          *,
          users_public!signer_id(name, email)
        `)
        .order('signature_timestamp', { ascending: false });

      // Fetch pending signature requests (real data now)
      const { data: pendingSigs } = await supabase
        .from('signature_requests')
        .select(`
          *,
          assigned_user:users_public!assigned_to(name, email),
          assigner:users_public!assigned_by(name, email)
        `)
        .eq('status', 'pending')
        .order('due_date', { ascending: true });

      setSignatures(completedSigs || []);
      setPendingSignatures(pendingSigs || []);
    } catch (error) {
      console.error('Error fetching signatures:', error);
      setSignatures([]);
      setPendingSignatures([]);
    } finally {
      setLoading(false);
    }
  };

  // Send reminder function
  const sendReminder = async (signatureRequestId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('send-reminder-email', {
        body: { signatureRequestId }
      });

      if (error) throw error;
      
      // Show success message and refresh data
      alert('Reminder sent successfully!');
      fetchSignatures();
    } catch (error) {
      console.error('Error sending reminder:', error);
      alert('Failed to send reminder. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="heading-section mb-2">Digital Signatures</h1>
              <p className="text-muted-foreground">Manage and track digital signatures for compliance documents</p>
            </div>
            <Link to="/signature-request">
              <Button className="cta-primary">
                <PenTool className="h-4 w-4 mr-2" />
                New Signature Request
              </Button>
            </Link>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="glass-panel border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold">{signatures.length}</div>
                <div className="text-sm text-muted-foreground">Completed Signatures</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-panel border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-400">{pendingSignatures.length}</div>
                <div className="text-sm text-muted-foreground">Pending Signatures</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-panel border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">98%</div>
                <div className="text-sm text-muted-foreground">Signature Rate</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-panel border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold">1.2</div>
                <div className="text-sm text-muted-foreground">Avg. Sign Time (days)</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Signatures Management */}
        <Tabs defaultValue="completed" className="space-y-6">
          <TabsList className="glass-panel border-0">
            <TabsTrigger value="completed">Completed Signatures</TabsTrigger>
            <TabsTrigger value="pending">Pending Signatures</TabsTrigger>
            <TabsTrigger value="templates">Signature Templates</TabsTrigger>
          </TabsList>

          <TabsContent value="completed">
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading signatures...</p>
                </div>
              ) : signatures.length > 0 ? (
                signatures.map((signature) => (
                  <Card key={signature.id} className="glass-panel border-0">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          <div className="p-2 glass-button rounded-lg">
                            <PenTool className="h-4 w-4" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold mb-2">
                              {signature.legal_statement || `${signature.document_type} Document`}
                            </h3>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4" />
                                <span>
                                  {signature.signer_name} ({signature.signer_role})
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                <span>
                                  {new Date(signature.signature_timestamp).toLocaleString()}
                                </span>
                              </div>
                            </div>
                            <Badge className="bg-blue-500/20 text-blue-300">
                              {signature.document_type}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-green-500/20 text-green-300">
                            Completed
                          </Badge>
                          <Button size="sm" variant="outline" className="glass-button">
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="text-center py-8">
                  <PenTool className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No completed signatures found</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="pending">
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading pending signatures...</p>
                </div>
              ) : pendingSignatures.length > 0 ? (
                pendingSignatures.map((pending) => (
                  <Card key={pending.id} className="glass-panel border-0">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          <div className="p-2 glass-button rounded-lg">
                            <Calendar className="h-4 w-4" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold mb-2">{pending.document_name}</h3>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4" />
                                <span>{pending.assigned_user?.name} ({pending.role_required})</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                <span>Due: {new Date(pending.due_date).toLocaleDateString()}</span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Badge className="bg-blue-500/20 text-blue-300">
                                {pending.document_type}
                              </Badge>
                              <Badge className={pending.priority === 'high' ? 'bg-red-500/20 text-red-300' : pending.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-300' : 'bg-green-500/20 text-green-300'}>
                                {pending.priority.toUpperCase()} Priority
                              </Badge>
                              {new Date(pending.due_date) < new Date() && (
                                <Badge className="bg-red-500/20 text-red-300">
                                  OVERDUE
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="glass-button"
                            onClick={() => sendReminder(pending.id)}
                          >
                            Send Reminder
                          </Button>
                          <Button 
                            size="sm" 
                            className="cta-primary"
                            onClick={() => window.open(`/signature-request/${pending.id}`, '_blank')}
                          >
                            View Document
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No pending signatures</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="templates">
            <Card className="glass-panel border-0">
              <CardContent className="pt-6">
                <div className="text-center py-12">
                  <PenTool className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">Signature Templates</h3>
                  <p className="text-muted-foreground mb-6">Create and manage digital signature templates for different document types.</p>
                  <Button className="cta-primary">
                    Create Template
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Signatures;